import { Button, Card, HStack, IconButton, Text, VStack } from '@chakra-ui/react';
import { MdEdit, MdPrint } from 'react-icons/md';
import { TextValueField } from '../../../../common/TextValueField';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { AttendanceStatement } from '../../../../../types/documents';
import { UploadIcon } from '../../local/statementListComponents/StatementTableItem';
import { DocumentStatus } from '../../../../../types/enums/DocumentStatus';
import { useSuIntl } from '../../../../../hooks/useSuIntl';
import { useDownloadDocument } from '../../export/useDownloadDocument';
import { FiDownload } from 'react-icons/fi';
import { UserDetailsCard } from '../../shared/UserDetailsCard';
import { UserCustomFieldValue } from '../../../../../types/customFields';
import { useEffect } from 'react';

type EditorSideMenuProps = {
    data: AttendanceStatement;
    onEdit: () => void;
    onDelete: () => void;
    isLoadingUpdate: boolean;
    onCustomFieldsLoaded?: (fields: UserCustomFieldValue[]) => void;
};

export const UploadedStatementSideMenu: React.FC<EditorSideMenuProps> = ({
    data,
    onEdit,
    onDelete,
    isLoadingUpdate,
    onCustomFieldsLoaded,
}) => {
    const { t, formatDate } = useSuIntl();
    const useDownload = useDownloadDocument();

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.ctrlKey && e.key === 'p') {
                e.preventDefault();
                useDownload.handlePrint(data);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    return (
        <VStack align="stretch" gap={6}>
            <Card boxShadow={'md'} p={4} borderRadius={'xl'} minW={270}>
                <HStack justify={'space-between'} align={'center'} mb={4}>
                    <Text fontSize={'lg'} fontWeight={'500'}>
                        {t('editor.options')}
                    </Text>
                    <UploadIcon size={25} status="uploaded" />
                </HStack>
                <HStack mb={6} align={'center'} justify={'space-between'}>
                    <TextValueField smallVariant label={t('field.createdAt')} value={formatDate(data.createdAt)} icon={<EditIcon />} />
                    <TextValueField smallVariant label={t('field.updatedAt')} value={formatDate(data.updatedAt)} icon={<MdEdit />} />
                </HStack>

                <VStack>
                    <Button
                        isLoading={isLoadingUpdate}
                        variant={'solid'}
                        w={'100%'}
                        onClick={onEdit}
                        leftIcon={<MdEdit />}
                        colorScheme={'green'}
                        isDisabled={data?.status === DocumentStatus.ARCHIVED}
                    >
                        {t('edit')}
                    </Button>

                    <HStack width={'100%'}>
                        <IconButton
                            size={'sm'}
                            onClick={() => useDownload.handlePrint(data)}
                            isLoading={useDownload.isLoading}
                            colorScheme="gray"
                            aria-label="print"
                            icon={<MdPrint size={26} />}
                            flex={1}
                        />
                        <IconButton
                            size={'sm'}
                            isLoading={useDownload.isLoading}
                            isDisabled={useDownload.isCompanyLoading}
                            onClick={() => useDownload.handleDownload(data)}
                            colorScheme="gray"
                            aria-label="print"
                            icon={<FiDownload size={26} />}
                            flex={1}
                        />
                    </HStack>

                    <Button
                        isDisabled={isLoadingUpdate}
                        onClick={onDelete}
                        variant={'ghost'}
                        w={'100%'}
                        leftIcon={<DeleteIcon />}
                        colorScheme="red"
                    >
                        {t('delete')}
                    </Button>
                </VStack>
            </Card>
            {data.userId && (
                <UserDetailsCard userId={data.userId} userName={data.data.data.name} onCustomFieldsLoaded={onCustomFieldsLoaded} />
            )}
        </VStack>
    );
};
